/**
 * Video Player Component
 * 
 * A reusable, WCAG-compliant video player control for play/pause toggling.
 * Handles keyboard accessibility, aria labels, and focus management.
 */

const initVideoPlayer = () => {
    const playPauseButtons = document.querySelectorAll('.z-video-playpause');
    
    playPauseButtons.forEach(button => {
        const video = button.closest('.z-video')?.querySelector('video');
        
        if (!video) return;
        
        // Set initial ARIA attributes
        updateButtonState(button, video);
        
        // Handle button click
        button.addEventListener('click', () => {
            toggleVideoPlayPause(video, button);
        });
        
        // Handle keyboard interaction (Enter and Space)
        button.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                toggleVideoPlayPause(video, button);
            }
        });
        
        // Update button state when video plays/pauses
        video.addEventListener('play', () => {
            updateButtonState(button, video);
        });
        
        video.addEventListener('pause', () => {
            updateButtonState(button, video);
        });
        
        // Make button keyboard focusable
        if (!button.hasAttribute('tabindex')) {
            button.setAttribute('tabindex', '0');
        }
    });

    initVideoScrollControl();
};

/**
 * Toggle video play/pause state
 */
const toggleVideoPlayPause = (video, button) => {
    if (video.paused) {
        video.dataset.userPaused = 'false';
        video.play().catch(() => {});
    } else {
        video.dataset.userPaused = 'true';
        video.pause();
    }
};

/**
 * Pause videos outside the viewport and resume them when visible again,
 * unless the user paused them manually.
 */
const initVideoScrollControl = () => {
    const videos = document.querySelectorAll('.z-video video');
    if (!videos.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target: video, isIntersecting }) => {
            if (isIntersecting) {
                if (video.dataset.userPaused !== 'true' && video.paused) {
                    video.play().catch(() => {});
                }
            } else if (!video.paused) {
                video.dataset.autoPaused = 'true';
                video.pause();
            }
        });
    }, { threshold: 0.25 });

    videos.forEach(video => {
        // Any pause not triggered by this observer is a user choice (e.g. native controls).
        video.addEventListener('pause', () => {
            if (video.dataset.autoPaused !== 'true') video.dataset.userPaused = 'true';
            delete video.dataset.autoPaused;
        });
        observer.observe(video);
    });
};

/**
 * Update button state to reflect video state
 */
const updateButtonState = (button, video) => {
    const isPlaying = !video.paused;
    const icon = button.querySelector('i');
    
    // Update aria-pressed for semantic state
    button.setAttribute('aria-pressed', isPlaying);
    
    // Update aria-label for screen readers
    const label = isPlaying ? 'Pause video' : 'Play video';
    button.setAttribute('aria-label', label);
    
    // Update icon and data attribute
    if (icon) {
        if (isPlaying) {
            icon.classList.remove('fa-play');
            icon.classList.add('fa-pause');
        } else {
            icon.classList.remove('fa-pause');
            icon.classList.add('fa-play');
        }
    }
    
    // Add visual state class for CSS
    button.classList.toggle('--playing', isPlaying);
};

export default initVideoPlayer;
